/* ==========================================================================
   Hideline — interactions.

   Four things live here:
     1. Reveal-on-scroll
     2. The bundle picker (real radios, no network round trip)
     3. Add to cart / Buy now, both through the Cart AJAX API
     4. The FAQ accordion (single-open, height-animated <details>)
     5. The mobile nav toggle

   Dawn owns the cart drawer element itself (cart-drawer.js) and the AJAX
   quantity plumbing (cart.js). This file talks to them, it does not replace
   them.

   Every timing here is from the design handoff. prefers-reduced-motion is
   honoured by skipping JS-driven animation entirely; CSS clamps the rest.
   ========================================================================== */

(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const prefersReducedMotion = () => REDUCED_MOTION.matches;

  const strings = window.hidelineStrings || {};

  /* ------------------------------------------------------------------------
     1. Reveal on scroll

     Elements marked [data-reveal] animate once. Above-the-fold elements fire
     immediately with a 40ms lead-in; the rest wait for the observer.
     ---------------------------------------------------------------------- */

  const REVEAL_ROOT_MARGIN = '-8% 0px -12% 0px';
  const REVEAL_THRESHOLD = 0.05;
  const REVEAL_LEAD_IN = 40;

  function initReveals() {
    const elements = Array.from(document.querySelectorAll('[data-reveal]'));
    if (elements.length === 0) return;

    const show = (el) => {
      el.classList.add('is-revealed');
      // Drop the compositor hint once the animation is done.
      el.addEventListener('animationend', () => el.classList.add('is-settled'), { once: true });
    };

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      elements.forEach(show);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
          window.setTimeout(() => show(el), delay);
          obs.unobserve(el);
        });
      },
      { rootMargin: REVEAL_ROOT_MARGIN, threshold: REVEAL_THRESHOLD }
    );

    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        const delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
        window.setTimeout(() => show(el), REVEAL_LEAD_IN + delay);
      } else {
        observer.observe(el);
      }
    });
  }

  /* ------------------------------------------------------------------------
     2. Bundle picker

     The three rows are the product's three variants. Every string the picker
     swaps was rendered by Liquid into the adjacent JSON block, so prices stay
     formatted by Shopify's money filter and Markets pricing is respected.
     ---------------------------------------------------------------------- */

  class HidelineBundlePicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = this.closest('form');
      this.variants = this.readVariants();

      this.addEventListener('change', this.onChange.bind(this));
      // Sync once on load: a ?variant= URL or a restored radio can disagree
      // with the server-rendered price block after a back-navigation.
      const checked = this.checkedInput();
      if (checked) this.apply(checked.value, { updateUrl: false });
    }

    readVariants() {
      const node = document.querySelector(`[data-hideline-variants="${this.sectionId}"]`);
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[hideline] could not parse variant data', error);
        return [];
      }
    }

    checkedInput() {
      return this.querySelector('.hl-bundle__input:checked');
    }

    onChange(event) {
      const input = event.target;
      if (!input.classList.contains('hl-bundle__input')) return;
      this.apply(input.value, { updateUrl: true });
    }

    apply(variantId, options) {
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return;

      const scope = this.form || document;
      const set = (selector, value) => {
        const el = scope.querySelector(selector);
        if (el) el.textContent = value;
      };

      const idInput = scope.querySelector('[data-hideline-variant-id]');
      if (idInput) idInput.value = variant.id;

      set('[data-hideline-total]', variant.price);
      set('[data-hideline-strike]', variant.strike);
      set('[data-hideline-unit]', variant.unit);
      set('[data-hideline-savings]', variant.savings);
      set('[data-hideline-add-label]', variant.addLabel);

      const addButton = scope.querySelector('[data-hideline-add]');
      const buyButton = scope.querySelector('[data-hideline-buy-now]');
      [addButton, buyButton].forEach((button) => {
        if (button) button.disabled = !variant.available;
      });

      this.clearError();

      if (options && options.updateUrl && variant.url) {
        try {
          window.history.replaceState({}, '', variant.url);
        } catch (error) {
          /* replaceState can throw in sandboxed contexts; the page still works */
        }
      }
    }

    clearError() {
      const error = (this.form || document).querySelector('[data-hideline-form-error]');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }
  }

  if (!customElements.get('hideline-bundle-picker')) {
    customElements.define('hideline-bundle-picker', HidelineBundlePicker);
  }

  /* ------------------------------------------------------------------------
     3. Add to cart / Buy now

     Both post to /cart/add.js. Add opens the drawer through Dawn's
     cart-drawer element (so focus trap and section rendering are Dawn's);
     Buy now goes straight to checkout, because the design's outline button
     cannot be Shopify's dynamic checkout button.
     ---------------------------------------------------------------------- */

  function drawerSectionsToRender() {
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function showFormError(form, message) {
    const error = form.querySelector('[data-hideline-form-error]');
    if (!error) return;
    error.textContent = message || strings.addError || '';
    error.hidden = false;
  }

  function setBusy(form, busy) {
    const buttons = form.querySelectorAll('[data-hideline-add], [data-hideline-buy-now]');
    buttons.forEach((button) => {
      button.toggleAttribute('aria-busy', busy);
      if (busy) {
        button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
      } else if (button.dataset.wasDisabled === 'false') {
        button.disabled = false;
      }
    });
  }

  function addToCart(form, { redirectToCheckout }) {
    const variantInput = form.querySelector('[data-hideline-variant-id]');
    if (!variantInput || !variantInput.value) return Promise.resolve();

    const drawer = document.querySelector('cart-drawer');
    const body = {
      items: [{ id: Number(variantInput.value), quantity: 1 }],
    };

    // Only ask for re-rendered sections when we are going to paint them.
    if (drawer && !redirectToCheckout) {
      body.sections = drawerSectionsToRender();
      body.sections_url = window.location.pathname;
    }

    setBusy(form, true);

    return fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          showFormError(form, data.description || data.message);
          return;
        }

        if (redirectToCheckout) {
          window.location.href = window.routes.cart_url.replace(/\/cart$/, '') + '/checkout';
          return;
        }

        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hideline-product-form', cartData: data });
        }
      })
      .catch((error) => {
        console.error('[hideline] add to cart failed', error);
        showFormError(form);
      })
      .finally(() => setBusy(form, false));
  }

  function initProductForms() {
    document.querySelectorAll('.js-hideline-product-form').forEach((form) => {
      // Section re-renders in the theme editor call this again; only bind once.
      if (form.dataset.hidelineBound === 'true') return;
      form.dataset.hidelineBound = 'true';

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        addToCart(form, { redirectToCheckout: false });
      });

      const buyNow = form.querySelector('[data-hideline-buy-now]');
      if (buyNow) {
        buyNow.addEventListener('click', () => {
          addToCart(form, { redirectToCheckout: true });
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     3b. Drawer quantity buttons

     The design has -/+ squares rather than a number field, so these call
     Dawn's CartItems.updateQuantity directly. Reaching 0 removes the line,
     which is what the design specifies for "- at 1".
     ---------------------------------------------------------------------- */

  function initDrawerQuantity() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-hideline-qty]');
      if (!button) return;

      const items = button.closest('cart-drawer-items') || button.closest('cart-items');
      if (!items || typeof items.updateQuantity !== 'function') return;

      const index = button.dataset.index;
      const current = parseInt(button.dataset.quantity || '0', 10);
      const next = button.dataset.hidelineQty === 'up' ? current + 1 : current - 1;

      const quantityInput = document.getElementById(`Drawer-quantity-${index}`);
      const variantId = quantityInput ? quantityInput.dataset.quantityVariantId : undefined;

      items.updateQuantity(index, Math.max(next, 0), event, button.getAttribute('name'), variantId);
    });
  }

  /* ------------------------------------------------------------------------
     4. FAQ accordion

     <details> for semantics; JS for single-open and the height transition.
     Height is measured from scrollHeight rather than the design's fixed
     max-height, so a long answer is never clipped. Timing is unchanged.
     ---------------------------------------------------------------------- */

  const FAQ_DURATION = 460;

  class HidelineAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details.hl-faq__item'));
      this.items.forEach((item) => {
        const summary = item.querySelector('summary');
        if (summary) summary.addEventListener('click', (event) => this.onSummaryClick(event, item));
      });
    }

    onSummaryClick(event, item) {
      if (prefersReducedMotion()) {
        // Let the browser toggle natively, just enforce single-open.
        if (!item.open) window.setTimeout(() => this.closeOthers(item), 0);
        return;
      }

      event.preventDefault();

      if (item.open) {
        this.collapse(item);
      } else {
        this.closeOthers(item);
        this.expand(item);
      }
    }

    closeOthers(current) {
      this.items.forEach((item) => {
        if (item !== current && item.open) {
          if (prefersReducedMotion()) {
            item.open = false;
          } else {
            this.collapse(item);
          }
        }
      });
    }

    expand(item) {
      const panel = item.querySelector('.hl-faq__answer');
      if (!panel) {
        item.open = true;
        return;
      }

      item.open = true;
      panel.classList.add('is-animating');
      panel.style.height = '0px';
      // Force layout so the transition has a start value.
      void panel.offsetHeight;
      panel.style.height = `${panel.scrollHeight}px`;

      this.after(panel, () => {
        panel.style.height = '';
        panel.classList.remove('is-animating');
      });
    }

    collapse(item) {
      const panel = item.querySelector('.hl-faq__answer');
      if (!panel) {
        item.open = false;
        return;
      }

      panel.classList.add('is-animating');
      panel.style.height = `${panel.scrollHeight}px`;
      void panel.offsetHeight;
      panel.style.height = '0px';

      this.after(panel, () => {
        item.open = false;
        panel.style.height = '';
        panel.classList.remove('is-animating');
      });
    }

    after(panel, done) {
      if (panel._hlTimer) window.clearTimeout(panel._hlTimer);
      panel._hlTimer = window.setTimeout(() => {
        panel._hlTimer = null;
        done();
      }, FAQ_DURATION);
    }
  }

  if (!customElements.get('hideline-accordion')) {
    customElements.define('hideline-accordion', HidelineAccordion);
  }

  /* ------------------------------------------------------------------------
     5. Mobile nav toggle

     Desktop-only design; this is part of the proposed mobile pass.
     ---------------------------------------------------------------------- */

  function initNavToggle() {
    const toggle = document.getElementById('HidelineNavToggle');
    const nav = document.getElementById('HidelineNav');
    if (!toggle || !nav) return;
    if (toggle.dataset.hidelineBound === 'true') return;
    toggle.dataset.hidelineBound = 'true';

    const setOpen = (open) => {
      nav.dataset.open = open ? 'true' : 'false';
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    toggle.addEventListener('click', () => setOpen(nav.dataset.open !== 'true'));
    nav.addEventListener('click', (event) => {
      if (event.target.tagName === 'A') setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && nav.dataset.open === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });
  }

  /* ---------------------------------------------------------------------- */

  function init() {
    initReveals();
    initProductForms();
    initDrawerQuantity();
    initNavToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', () => {
    initReveals();
    initProductForms();
    initNavToggle();
  });
})();
